import { Box, Stack } from "@mui/material";
import { ContentState, Editor, EditorState, Modifier } from "draft-js";
import type { DraftHandleValue } from "draft-js";
import { FC, RefObject } from "react";
import UploadImage from "./UploadImage";
import SentimentSliderWithChips from "./SentimentSliderWithChips";
import PaidContentLine from "./PaidContentLine";
import { fontSizeStyles, mediaBlockRenderer } from "./utilsLongPost";
import MentionDropdown from "../CreatePostModal/component/MentionDropdown";

interface PostEditorFormProps {
  lineYReady: boolean;
  formContainerRef: RefObject<HTMLDivElement | null>;
  editorContainerRef: RefObject<HTMLDivElement | null>;
  title: string;
  setTitle: (value: string) => void;
  imagePreview: string | null;
  fileInputRef: RefObject<HTMLInputElement | null>;
  handleSelectImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: () => void;
  editPost: any;
  summaryEditorRef: RefObject<HTMLDivElement | null>;
  bodyEditorRef: RefObject<HTMLDivElement | null>;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  editorStateSummary: any;
  handleEditorChange: (state: any, type: "summary" | "body" | "title") => void;
  handleKeyCommand: (command: string, state: any) => "handled" | "not-handled";
  getBlockStyle: (block: any) => string;
  searchTerm: string;
  isSearchFetching: boolean;
  autocompleteResults: { data: { name: string; code: string; type: string } }[];
  dropdownPos: { top: number; left: number };
  highlightedIndex: number;
  insertMention: (mention: { data: { code: string } }) => void;
  slider: number;
  setSlider: (val: number) => void;
  chipSelect: string[];
  handleChipClick: (label: string) => void;
  editorStateBody: any;
  isPaidContent: boolean;
  lineRef: RefObject<HTMLDivElement | null>;
  handleMouseDown: (e: React.MouseEvent) => void;
  togglePaidContent: boolean;
  setTogglePaidContent: (val: boolean) => void;
  allowAnyAmount: boolean;
  setAllowAnyAmount: (val: boolean) => void;
  price: string;
  setPrice: React.Dispatch<React.SetStateAction<string>>;
  setIsPaidContent: React.Dispatch<React.SetStateAction<boolean>>;
  lineY: number;
  handleUndo: () => void;
  handleRedo: () => void;
  setSelectedSize: React.Dispatch<React.SetStateAction<string>>;
  applyFontSizeAndBlockData: (size: string, align?: string) => void;
  selectedSize: string;

  fontSizeOptions: any[];
  toggleInlineStyle: (style: string) => void;
  toggleBlockData: (key: string, value: string) => void;
  fileInputEditorRef: RefObject<HTMLInputElement | null>;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBodyPastedFiles: (files: Blob[]) => DraftHandleValue;
  invalidPrice?: boolean;
  setActiveField: (field: "summary" | "body" | "title") => void;
  editorStateTitle: any;
  setInvalidPrice: any;
  maxTotalChars: number;
}

const PostEditorForm: FC<PostEditorFormProps> = ({
  // title,
  // setTitle,
  lineYReady,
  formContainerRef,
  editorContainerRef,
  bodyEditorRef,
  summaryEditorRef,
  imagePreview,
  fileInputRef,
  handleSelectImage,
  handleRemoveImage,
  editPost,
  handleKeyDown,
  editorStateSummary,
  handleEditorChange,
  handleKeyCommand,
  getBlockStyle,
  searchTerm,
  autocompleteResults,
  dropdownPos,
  highlightedIndex,
  insertMention,
  slider,
  setSlider,
  chipSelect,
  handleChipClick,
  editorStateBody,
  isPaidContent,
  lineRef,
  handleMouseDown,
  togglePaidContent,
  setTogglePaidContent,
  allowAnyAmount,
  setAllowAnyAmount,
  price,
  setPrice,
  lineY,
  setActiveField,
  editorStateTitle,
  setInvalidPrice,
  maxTotalChars,
  handleBodyPastedFiles,
}) => {
  const getSummaryLength = () => {
    if (!editorStateSummary) return 0;
    const plainText = editorStateSummary.getCurrentContent().getPlainText("");
    return plainText.length;
  };

  const getBodyLength = () => {
    if (!editorStateBody) return 0;
    return editorStateBody.getCurrentContent().getPlainText("").length;
  };

  const summaryLength = getSummaryLength();
  const remaining = 500 - summaryLength;
  const isOverLimit = remaining < 0;

  const totalLength = summaryLength + getBodyLength();
  const totalRemaining = maxTotalChars - totalLength;
  const isTotalOverLimit = totalRemaining < 0;

  const makePasteHandler =
    (field: "summary" | "body" | "title") =>
    (text: string, _html: string | undefined, editorState: EditorState): DraftHandleValue => {
      if (!text) return "not-handled";
      const fragment = ContentState.createFromText(text.replace(/\r\n?/g, "\n")).getBlockMap();
      const nextContent = Modifier.replaceWithFragment(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        fragment
      );
      const nextState = EditorState.push(editorState, nextContent, "insert-fragment");
      handleEditorChange(nextState, field);
      return "handled";
    };

  return (
    <>
      <Box
        ref={formContainerRef}
        sx={{
          position: "relative",
          width: "800px !important",
          marginTop: "21px",
        }}
      >
        <Stack>
          <div>
            <Box
              sx={{
                minHeight: "52px",
                marginBottom: "12px",
                "& .public-DraftEditor-content": {
                  fontSize: "44px",
                  fontWeight: "bold",
                  color: "inherit",
                  lineHeight: "52px",
                },
                "& .public-DraftEditorPlaceholder-root": {
                  position: "absolute",
                  height: "52px",
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "44px",
                  fontWeight: 600,
                  lineHeight: "52px",
                  letterSpacing: "0%",
                  color: "#9DA6B0",
                  pointerEvents: "none",
                },
              }}
              onClick={() => setActiveField("title")}
            >
              <Editor
                editorState={editorStateTitle}
                onChange={(state) => handleEditorChange(state, "title")}
                handleKeyCommand={handleKeyCommand}
                placeholder="Title"
                blockStyleFn={getBlockStyle}
                customStyleMap={fontSizeStyles}
                spellCheck={true}
              />
            </Box>

            <UploadImage
              imagePreview={imagePreview}
              fileInputRef={fileInputRef}
              handleSelectImage={handleSelectImage}
              handleRemoveImage={handleRemoveImage}
              editPost={editPost}
            />

            {/* Summary Editor Section */}
            <div
              style={{
                minHeight: "152px",
                height: "auto",
                width: "100%",
                fontSize: "15px",
                backgroundColor: "#fff",
                overflow: "visible",
                position: "relative",
                borderBottom: "1px solid #E4E7E9",
                marginTop: "22px",
                padding: "0 0 0px 0",
              }}
              className="editorRoot"
              ref={summaryEditorRef}
              onKeyDown={handleKeyDown}
              onClick={() => {
                setActiveField("summary");
                const editor = document.querySelector(".DraftEditor-root") as HTMLElement;
                editor?.focus();
                document
                  .querySelector(".DraftEditor-root")
                  ?.dispatchEvent(new MouseEvent("mousedown"));
              }}
            >
              <Box
                className="editor-wrapper"
                sx={{
                  minHeight: "120px",
                  "& .public-DraftEditorPlaceholder-root": {
                    fontSize: "16px",
                  },
                  "& figure": {
                    margin: 0,
                  },
                }}
              >
                <Editor
                  editorState={editorStateSummary}
                  onChange={(state) => handleEditorChange(state, "summary")}
                  handlePastedText={makePasteHandler("summary")}
                  handleKeyCommand={handleKeyCommand}
                  blockStyleFn={getBlockStyle}
                  placeholder="Article summary (include relevant $cashtags)"
                  customStyleMap={fontSizeStyles}
                  blockRendererFn={mediaBlockRenderer}
                  spellCheck={true}
                />
              </Box>

              <Box
                sx={{
                  textAlign: "right",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: summaryLength > 0 ? (isOverLimit ? "#CE212B" : "#2A2D31") : "#C6CACE",
                  paddingBottom: "22px",
                }}
              >
                {isOverLimit
                  ? `Text must be 500 characters or less -${Math.abs(remaining)}`
                  : remaining}
              </Box>
            </div>
          </div>
        </Stack>

        <Stack gap="22px" sx={{ marginTop: "22px" }}>
          <SentimentSliderWithChips
            slider={slider}
            setSlider={setSlider}
            chipSelect={chipSelect}
            handleChipClick={handleChipClick}
          />
        </Stack>
        <div
          ref={editorContainerRef}
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <div
            style={{
              visibility: lineYReady ? "visible" : "hidden",
              padding: togglePaidContent ? "22px 0 385px 0" : "22px 0 385px 0",
              minHeight: "456px",
              width: "100%",
              fontSize: "15px",
              backgroundColor: "#fff",
              position: "relative",
              margin: "0",
            }}
            className="editorRoot"
            ref={bodyEditorRef}
            onKeyDown={handleKeyDown}
            onClick={() => {
              const editor = document.querySelector(".DraftEditor-root") as HTMLElement;
              editor?.focus();
              document
                .querySelector(".DraftEditor-root")
                ?.dispatchEvent(new MouseEvent("mousedown"));
            }}
          >
            <Box sx={{ "& figure": { margin: 0 } }}>
              <Editor
                editorState={editorStateBody}
                onChange={(state) => handleEditorChange(state, "body")}
                handlePastedText={makePasteHandler("body")}
                handlePastedFiles={handleBodyPastedFiles}
                handleKeyCommand={handleKeyCommand}
                blockStyleFn={getBlockStyle}
                placeholder="Add a article text..."
                customStyleMap={fontSizeStyles}
                blockRendererFn={mediaBlockRenderer}
                onFocus={() => setActiveField("body")}
                spellCheck={true}
              />
            </Box>
            {isPaidContent && (
              <PaidContentLine
                lineRef={lineRef}
                handleMouseDown={handleMouseDown}
                togglePaidContent={togglePaidContent}
                setTogglePaidContent={setTogglePaidContent}
                allowAnyAmount={allowAnyAmount}
                setAllowAnyAmount={setAllowAnyAmount}
                price={price}
                setPrice={setPrice}
                lineY={lineY}
                setInvalidPrice={setInvalidPrice}
                isEditing={editPost!!}
              />
            )}
          </div>
        </div>

        <Box
          sx={{
            textAlign: "right",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "24px",
            color: totalLength > 0 ? (isTotalOverLimit ? "#CE212B" : "#2A2D31") : "#C6CACE",
            paddingBottom: "22px",
          }}
        >
          {isTotalOverLimit
            ? `Text must be ${maxTotalChars.toLocaleString()} characters or less -${Math.abs(
                totalRemaining
              ).toLocaleString()}`
            : `${totalRemaining.toLocaleString()} / ${maxTotalChars.toLocaleString()} characters left`}
        </Box>

        {searchTerm && (
          <MentionDropdown
            items={autocompleteResults}
            highlightedIndex={highlightedIndex}
            onSelect={insertMention}
            position={dropdownPos}
          />
        )}
      </Box>
    </>
  );
};

export default PostEditorForm;
